import Phaser from "phaser";
import type { GameApp } from "../app/GameApp";
import type { HeroState, ItemStack } from "@/core";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";
import {
  EQUIPMENT_CATALOG, SLOT_ORDER, slotLabel, computeCritBonusPct,
  type EquipmentDef, type EquipmentSlot, type Stat, type ArmorClass,
} from "../core/roscha";

const ARMOR_CLASS_LABEL: Record<ArmorClass, string> = {
  light: "Лёгкая",
  medium: "Средняя",
  heavy: "Тяжёлая",
};

function armorClassLabel(c: ArmorClass): string {
  return ARMOR_CLASS_LABEL[c];
}

const STAT_SHORT: Record<Stat, string> = {
  strength: "СИЛ",
  dexterity: "ЛОВ",
  intelligence: "ИНТ",
  wisdom: "МУД",
  constitution: "ТЕЛ",
};

export class EquipmentOverlay extends Phaser.Scene {
  private app!: GameApp;
  private overlay?: DomOverlay;
  private slotsEl?: HTMLElement;
  private itemsEl?: HTMLElement;
  private statsEl?: HTMLElement;
  private unsubWorld?: () => void;
  private onWake = () => this.refresh();

  constructor() {
    super("EquipmentOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.mount();
    this.refresh();
    this.unsubWorld = this.app.onWorldState(() => this.refresh());
    this.events.on(Phaser.Scenes.Events.WAKE, this.onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private mount() {
    const bodyHtml = `
      <div class="equipment__cols">
        <section class="equipment__col">
          <h3 class="dom-section__title">Слоты</h3>
          <div class="equipment__slots" data-bind="slots"></div>
        </section>
        <section class="equipment__col">
          <h3 class="dom-section__title">Инвентарь</h3>
          <div class="equipment__items" data-bind="items"></div>
        </section>
      </div>
      <div class="equipment__stats" data-bind="stats"></div>
    `;

    this.overlay = mountOverlay(this, {
      title: "Экипировка",
      subtitle: "Управляй снаряжением героя",
      bodyHtml,
      onClose: () => this.app.backToMap(),
    });

    const root = this.overlay.root;
    this.slotsEl = root.querySelector<HTMLElement>('[data-bind="slots"]') ?? undefined;
    this.itemsEl = root.querySelector<HTMLElement>('[data-bind="items"]') ?? undefined;
    this.statsEl = root.querySelector<HTMLElement>('[data-bind="stats"]') ?? undefined;

    // Event delegation: clicks на slots/items handled by root listener (selectors)
    root.addEventListener("click", (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const unequipBtn = target.closest<HTMLElement>('[data-action="unequip"]');
      if (unequipBtn) {
        const slot = unequipBtn.dataset.slot as EquipmentSlot;
        this.app.unequipSlot(slot);
        return;
      }
      const equipBtn = target.closest<HTMLElement>('[data-action="equip"]');
      if (equipBtn && equipBtn.dataset.disabled !== "true") {
        const id = equipBtn.dataset.itemId!;
        this.app.equipItem(id);
      }
    });
  }

  private refresh() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero || !this.overlay) return;

    if (this.slotsEl) {
      this.slotsEl.innerHTML = SLOT_ORDER.map((slot) => this.renderSlot(slot, hero)).join("");
    }

    if (this.itemsEl) {
      const equipable = hero.inventory.items.filter((it) => EQUIPMENT_CATALOG[it.itemId] != null);
      this.itemsEl.innerHTML =
        equipable.length === 0
          ? `<p class="equipment__empty">Нет снаряжения. Побеждай врагов, чтобы получить лут.</p>`
          : equipable.map((stack) => this.renderItem(stack, hero)).join("");
    }

    if (this.statsEl) this.statsEl.innerHTML = this.renderStatsRow(hero);
  }

  private renderSlot(slot: EquipmentSlot, hero: HeroState): string {
    const itemId = hero.equipment[slot];
    const item = itemId ? EQUIPMENT_CATALOG[itemId] : null;
    if (!item) {
      return `
        <div class="equipment__slot equipment__slot--empty">
          <span class="equipment__slot-label">${slotLabel(slot)}</span>
          <span class="equipment__slot-empty">пусто</span>
        </div>
      `;
    }
    const tag = item.armorClass ? `<span class="equipment__tag equipment__tag--${item.armorClass}">${armorClassLabel(item.armorClass)}</span>` : "";
    return `
      <div class="equipment__slot equipment__slot--filled">
        <div class="equipment__slot-info">
          <span class="equipment__slot-label">${slotLabel(slot)}</span>
          <span class="equipment__slot-name">${item.name}${tag}</span>
          <span class="equipment__slot-bonus">${this.itemBonus(item)}</span>
        </div>
        <button class="dom-btn equipment__unequip" data-action="unequip" data-slot="${slot}">Снять</button>
      </div>
    `;
  }

  private renderItem(stack: ItemStack, hero: HeroState): string {
    const item = EQUIPMENT_CATALOG[stack.itemId];
    if (!item) return "";
    const equipped = hero.equipment[item.slot] === stack.itemId;
    const tag = item.armorClass ? `<span class="equipment__tag equipment__tag--${item.armorClass}">${armorClassLabel(item.armorClass)}</span>` : "";
    return `
      <div class="equipment__item ${equipped ? "is-equipped" : ""}">
        <div class="equipment__item-info">
          <span class="equipment__item-name">${item.name}${tag}</span>
          <span class="equipment__item-meta">${slotLabel(item.slot)} · ${this.itemBonus(item)}</span>
        </div>
        <button class="dom-btn ${equipped ? "" : "dom-btn--success"} equipment__equip"
                data-action="equip" data-item-id="${item.id}" data-disabled="${equipped}">
          ${equipped ? "Надето" : "Надеть"}
        </button>
      </div>
    `;
  }

  private itemBonus(item: EquipmentDef): string {
    const parts: string[] = [];
    for (const [stat, bonus] of Object.entries(item.statBonuses)) {
      if (!bonus) continue;
      const sign = bonus > 0 ? "+" : "";
      parts.push(`${sign}${bonus} ${STAT_SHORT[stat as Stat]}`);
    }
    if (item.armor !== 0) parts.push(`${item.armor > 0 ? "+" : ""}${item.armor} АРМОР`);
    if (item.evasionMod !== 0) parts.push(`${item.evasionMod > 0 ? "+" : ""}${item.evasionMod} УКЛ`);
    if (item.critBonusPct) parts.push(`+${item.critBonusPct}% КРИТ`);
    if (item.grantsCards.length > 0) parts.push(`+${item.grantsCards.length} карт`);
    return parts.join(" · ") || "—";
  }

  private renderStatsRow(hero: HeroState): string {
    const critPct = computeCritBonusPct(hero);

    const colorCls = (eff: number, base: number) =>
      eff < base ? "stat--debuff" : eff > base ? "stat--buff" : "stat--equal";

    const cell = (label: string, eff: number, base: number, suffix = "") =>
      `<span class="${colorCls(eff, base)}">${label} <strong>${eff}${suffix}</strong></span>`;

    const statCell = (label: string, key: keyof typeof hero.stats) =>
      cell(label, hero.stats[key] ?? 0, hero.baseStats[key] ?? 0);

    const baseDex = hero.baseStats.dexterity ?? 0;
    const baselineEvasion = baseDex + 3;

    return `
      <span>HP <strong>${hero.hp}/${hero.maxHp}</strong></span>
      ${cell("Армор", hero.armor, 0)}
      ${cell("Уклон", hero.evasion, baselineEvasion)}
      <span>Крит <strong>${5 + critPct}%</strong></span>
      ${statCell("СИЛ", "strength")}
      ${statCell("ЛОВ", "dexterity")}
      ${statCell("ИНТ", "intelligence")}
      ${statCell("МУД", "wisdom")}
      ${statCell("ТЕЛ", "constitution")}
    `;
  }

  private cleanup() {
    this.events.off(Phaser.Scenes.Events.WAKE, this.onWake);
    this.unsubWorld?.();
    this.unsubWorld = undefined;
    this.overlay = undefined;
    this.slotsEl = undefined;
    this.itemsEl = undefined;
    this.statsEl = undefined;
  }
}
